<script>
/**
 * @component Home
 * @description Dashboard principal. Doctor/Secretary: saludo, stats, acciones rápidas.
 * Admin/HealthAdmin: dashboard con tablas y actividad reciente.
 * Paciente: acciones rápidas, próximos turnos, recetas emitidas.
 * @example <Home />
 */
import BaseSubNav from "@/components/ui/BaseSubNav.vue";
import { mapGetters } from "vuex";
import { capitalize } from "@/services/helper";
import {
  landingUrl,
  isMonitoringEnabled,
  isProtocolsEnabled,
  isProgramsEnabled,
} from "@/services/config";
import appointmentService from "@/services/appointment";
import {
  hidesAppointmentDoctor,
  appointmentInstitutionName,
} from "@/services/procedure-appointment";
import indicationService from "@/services/indication";
import memberService from "@/services/member";
// UC-REG-295: banner de la credencial municipal (Campana) en el Home del
// paciente — armado desde el User logueado, sin Member.
import {
  isMunicipalCredential,
  toMunicipalCredential,
} from "@/services/credential";
import MunicipalCredentialCard from "@/components/patient/MunicipalCredentialCard.vue";
// UC-REG-304: tarjetas del Home del paciente según el portal del tenant
// (Campana: sin "Tareas de salud", "Mis prescripciones").
import { patientPortal } from "@/services/patient-portal";
import dashboardService from "@/services/dashboard";
import validationService from "@/services/validation";
import slotService from "@/services/slot";
import { isFeatureEnabled } from "@/directives/has-feature";
import {
  getQuickActions,
  getAdminQuickActions,
} from "@/services/home-quick-actions";

export default {
  name: "Home",

  components: {
    BaseSubNav,
    MunicipalCredentialCard,
  },

  data() {
    return {
      isMonitoringEnabled,
      isProtocolsEnabled,
      isProgramsEnabled,
      stats: {
        activeMembers: 0,
        authorizationsToday: 0,
        pendingAudit: 0,
        activeProviders: 0,
        monthlyServiceRecords: 0,
      },
      recentAuthorizations: [],
      recentActivity: [],
      isLoadingDashboard: false,
      // Patient data
      patientAppointments: [],
      patientMedications: [],
      isLoadingAppointments: false,
      isLoadingMedications: false,
      // Member credential
      memberData: null,
      isMember: false,
      /** @type {boolean} UC-REG-295: credencial municipal (sin Member) */
      isMunicipalCredential,
      /** @type {Object} UC-REG-304: labels y visibilidad del portal del paciente */
      patientPortal,
      // Doctor dashboard stats
      doctorStats: {
        patientsLast30: null,
        todayAppointments: null,
        next7DaysSlots: null,
        monthValidations: null,
      },
      isLoadingDoctorStats: false,
    };
  },

  computed: {
    ...mapGetters([
      "isDoctor",
      "isPatient",
      "isAdmin",
      "isAdmissionist",
      "isAssistant",
      "isSecretary",
      "isHealthAdmin",
      "hasClinicalAccess",
      "hasCapability",
      "user",
    ]),

    /** @returns {boolean} Si el usuario tiene acceso administrativo */
    isAdminLike() {
      return this.isAdmin || this.isHealthAdmin;
    },

    /** @returns {boolean} Si el usuario ve el dashboard de doctor */
    isDoctorDashboard() {
      return (
        !this.isAdminLike &&
        !this.isPatient &&
        (this.isDoctor || this.isSecretary || this.hasClinicalAccess)
      );
    },

    /** @returns {string} Nombre de saludo */
    userName() {
      if (!this.user || !this.user.firstName) return "";
      return `Hola, ${capitalize(this.user.firstName)}`;
    },

    /** @returns {string} Fecha actual formateada */
    todayDate() {
      const d = new Date();
      const options = {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      };
      const formatted = d.toLocaleDateString("es-AR", options);
      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    },

    /** @returns {string} URL del landing */
    landingUrl() {
      return landingUrl;
    },

    /** @returns {Array} Próximos 3 turnos del paciente */
    upcomingAppointments() {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      return this.patientAppointments
        .filter((a) => new Date(a.startsAt) >= now && a.status !== "cancelled")
        .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))
        .slice(0, 3);
    },

    /** @returns {Array} Últimas 4 recetas del paciente */
    recentMedications() {
      return this.patientMedications.slice(0, 4);
    },

    /**
     * @description UC-REG-295: credencial municipal del User logueado.
     * @returns {Object|null}
     */
    municipalCredential() {
      return toMunicipalCredential(this.user);
    },

    /** @returns {string} Nombre del afiliado para la mini credencial */
    credentialName() {
      if (!this.memberData) return "";
      return `${this.memberData.lastName || ""}, ${
        this.memberData.firstName || ""
      }`.trim();
    },

    /** @returns {string} Número de afiliado */
    credentialNumber() {
      return this.memberData?.memberNumber || "—";
    },

    /**
     * @returns {string} Plan del afiliado — sale del Membership vigente
     * (`memberData.membership.plan`), no de `memberData.plan` (legacy).
     */
    credentialPlan() {
      const plan = this.memberData?.membership?.plan;
      if (!plan) return "";
      return typeof plan === "object" ? plan.name || "" : plan;
    },

    /**
     * @description Acciones rápidas según rol. UC-REG-256: la lógica vive en
     *              `services/home-quick-actions.js` (testeable sin montar el
     *              SFC); "Agendar turnos" respeta `clinic.appointments`.
     * @returns {Array}
     */
    quickActions() {
      return getQuickActions({ isSecretary: this.isSecretary });
    },

    /**
     * @description UC-REG-249 — true si al menos UNA de las features que
     *              alimentan el dashboard OOSS del admin está encendida.
     *              Cuando todas están off (ej. Campana, consultorio puro),
     *              se omite el fetch y se muestran acciones rápidas en vez
     *              de tarjetas eternamente en cero. Default-ON: sin
     *              overrides seteados, el dashboard se ve como siempre.
     * @returns {boolean}
     */
    hasAnyOossDashboardData() {
      return [
        "ooss.members",
        "ooss.authorizations",
        "ooss.providers",
        "ooss.audit",
        "ooss.validator",
      ].some((key) => isFeatureEnabled(key));
    },

    /**
     * @description Acciones rápidas del fallback admin (dashboard OOSS
     *              apagado). Solo módulos que un admin puede abrir.
     * @returns {Array}
     */
    adminQuickActions() {
      // UC-REG-256: ver `services/home-quick-actions.js`.
      return getAdminQuickActions();
    },
  },

  filters: {
    /**
     * @description Formatea número con separador de miles
     * @param {number} v
     * @returns {string}
     */
    numFormat(v) {
      if (v == null) return "—";
      return new Intl.NumberFormat("es-AR").format(v);
    },
  },

  created() {
    // UC-REG-249: si el dashboard OOSS está apagado entero para la org, ni
    // siquiera pedimos los datos (las tarjetas tampoco se renderizan).
    if (this.isAdminLike && this.hasAnyOossDashboardData) {
      this.loadDashboard();
    }
    if (this.isDoctorDashboard) {
      this.loadDoctorStats();
    }
    if (this.isPatient) {
      // UC-REG-295: en modo municipal la credencial no depende de un Member.
      if (!this.isMunicipalCredential) {
        this.loadMemberData();
      }
      this.loadPatientAppointments();
      this.loadPatientMedications();
    }
  },

  methods: {
    /**
     * @description Carga las 4 métricas del dashboard de doctor/secretaria.
     * Usa slotService.getSlotsByDoctor como fuente (igual que ScheduleView/sala de espera)
     * para que los números coincidan con lo que el doctor ve en su agenda.
     * - Stat 1: slots con turno asignado en últimos 30 días (= atenciones realizadas)
     * - Stat 2: slots con turno asignado hoy
     * - Stat 3: todos los turnos (libres + ocupados) para los próximos 7 días
     * - Stat 4: validaciones del mes calendario actual
     * Las llamadas son paralelas con Promise.allSettled.
     * @returns {Promise<void>}
     */
    async loadDoctorStats() {
      this.isLoadingDoctorStats = true;
      try {
        const now = new Date();
        const doctorId = this.user._id;

        const thirtyDaysAgo = new Date(now);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        const startOfToday = new Date(now);
        startOfToday.setHours(0, 0, 0, 0);
        const endOfToday = new Date(now);
        endOfToday.setHours(23, 59, 59, 999);

        const endOf7Days = new Date(startOfToday);
        endOf7Days.setDate(endOf7Days.getDate() + 6);
        endOf7Days.setHours(23, 59, 59, 999);

        const year = now.getFullYear();
        const month = now.getMonth() + 1;

        const [last30Result, todayResult, next7Result, validationsResult] =
          await Promise.allSettled([
            // Stat 1: slots ocupados (con turno) en últimos 30 días
            slotService.getSlotsByDoctor({
              doctorId,
              params: {
                startsAt: {
                  $gte: thirtyDaysAgo.toISOString(),
                  $lte: now.toISOString(),
                },
                appointment: { $exists: true },
                limit: 1,
              },
            }),
            // Stat 2: slots ocupados (con turno) hoy
            slotService.getSlotsByDoctor({
              doctorId,
              params: {
                startsAt: { $gte: startOfToday.toISOString() },
                endsAt: { $lte: endOfToday.toISOString() },
                appointment: { $exists: true },
                limit: 1,
              },
            }),
            // Stat 3: turnos ocupados (con appointment) próximos 7 días
            slotService.getSlotsByDoctor({
              doctorId,
              params: {
                startsAt: {
                  $gte: startOfToday.toISOString(),
                  $lte: endOf7Days.toISOString(),
                },
                appointment: { $exists: true },
                limit: 1,
              },
            }),
            // Stat 4: validaciones del mes calendario actual. Si el
            // validador está apagado en la org (UC-REG-249) ni lo pedimos —
            // el endpoint devolvería 404 y el stat va oculto en el template.
            isFeatureEnabled("ooss.validator")
              ? validationService.get({ year, month })
              : Promise.resolve(null),
          ]);

        if (last30Result.status === "fulfilled" && last30Result.value) {
          this.doctorStats.patientsLast30 = last30Result.value.total ?? 0;
        }

        if (todayResult.status === "fulfilled" && todayResult.value) {
          this.doctorStats.todayAppointments = todayResult.value.total ?? 0;
        }

        if (next7Result.status === "fulfilled" && next7Result.value) {
          this.doctorStats.next7DaysSlots = next7Result.value.total ?? 0;
        }

        if (
          validationsResult.status === "fulfilled" &&
          validationsResult.value
        ) {
          this.doctorStats.monthValidations =
            validationsResult.value.total ?? 0;
        }
      } catch (e) {
        // Silent fail — stats quedan en null y se muestran como "—"
      } finally {
        this.isLoadingDoctorStats = false;
      }
    },

    /**
     * @description Carga los datos reales del dashboard desde el backend
     */
    async loadDashboard() {
      this.isLoadingDashboard = true;
      try {
        const data = await dashboardService.get();
        this.stats = data.metrics;
        this.recentAuthorizations = (data.recentAuthorizations || []).filter(
          (a) => a.status === "pending"
        );
        this.recentActivity = data.recentActivity || [];
      } catch (e) {
        this.$message.error("Error al cargar el dashboard");
      } finally {
        this.isLoadingDashboard = false;
      }
    },

    /**
     * @description Carga datos del afiliado para la mini credencial. Si 404, no es afiliado.
     */
    async loadMemberData() {
      const silentOpts = { getErrorMessage: () => null };
      try {
        const data = await memberService.getMe(silentOpts);
        if (data) {
          this.memberData = data;
          this.isMember = true;
        }
      } catch {
        this.isMember = false;
      }
    },

    handleAgendaClick() {
      window.open(landingUrl, "_blank");
    },

    /**
     * @description Formatea fecha ISO a dd/MM/yyyy
     * @param {string} dateStr - Fecha ISO
     * @returns {string}
     */
    formatShortDate(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      return d.toLocaleDateString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    /**
     * @description Devuelve tiempo relativo desde una fecha ISO
     * @param {string} dateStr - Fecha ISO
     * @returns {string}
     */
    timeAgo(dateStr) {
      if (!dateStr) return "";
      const now = new Date();
      const date = new Date(dateStr);
      const diffMs = now - date;
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return "Ahora";
      if (diffMin < 60) return `Hace ${diffMin} min`;
      const diffHours = Math.floor(diffMin / 60);
      if (diffHours < 24) return `Hace ${diffHours} h`;
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays === 1) return "Ayer";
      return `Hace ${diffDays} días`;
    },

    /**
     * @description Devuelve la clase CSS para un badge de estado
     * @param {string} status - Estado
     * @returns {string}
     */
    getStatusClass(status) {
      const map = {
        approved: "badge--success",
        pending: "badge--warning",
        rejected: "badge--danger",
      };
      return map[status] || "badge--neutral";
    },

    /**
     * @description Devuelve el label de un estado
     * @param {string} status - Estado
     * @returns {string}
     */
    getStatusLabel(status) {
      const map = {
        approved: "Aprobada",
        pending: "Pendiente",
        rejected: "Rechazada",
      };
      return map[status] || status;
    },

    /**
     * @description Devuelve la clase del indicador de actividad
     * @param {string} type - Tipo
     * @returns {string}
     */
    getActivityClass(type) {
      return `activity__dot--${type}`;
    },

    /**
     * @description Navega a la ruta de una acción rápida
     * @param {string} routeName - Nombre de la ruta
     */
    goToAction(routeName) {
      this.$router.push({ name: routeName });
    },

    /**
     * @description Carga los turnos del paciente logueado
     */
    async loadPatientAppointments() {
      this.isLoadingAppointments = true;
      try {
        const data = await appointmentService.getMyAppointments(this.user._id);
        const raw = Array.isArray(data)
          ? data
          : data?.appointments || data?.docs || [];
        this.patientAppointments = raw;
      } catch {
        this.patientAppointments = [];
      } finally {
        this.isLoadingAppointments = false;
      }
    },

    /**
     * @description Carga las recetas (medications) del paciente logueado
     */
    async loadPatientMedications() {
      this.isLoadingMedications = true;
      try {
        const silentOpts = { getErrorMessage: () => null };
        const data = await indicationService.get(
          this.user._id,
          [{ type: "medications" }],
          silentOpts
        );
        const raw = Array.isArray(data) ? data : [];
        this.patientMedications = raw.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
      } catch {
        this.patientMedications = [];
      } finally {
        this.isLoadingMedications = false;
      }
    },

    /**
     * @description Formatea día del turno
     * @param {string} dateStr - Fecha ISO
     * @returns {string}
     */
    getDay(dateStr) {
      return new Date(dateStr).getDate().toString().padStart(2, "0");
    },

    /**
     * @description Formatea mes del turno
     * @param {string} dateStr - Fecha ISO
     * @returns {string}
     */
    getMonth(dateStr) {
      const months = [
        "ENE",
        "FEB",
        "MAR",
        "ABR",
        "MAY",
        "JUN",
        "JUL",
        "AGO",
        "SEP",
        "OCT",
        "NOV",
        "DIC",
      ];
      return months[new Date(dateStr).getMonth()];
    },

    /**
     * @description Formatea hora del turno
     * @param {string} dateStr - Fecha ISO
     * @returns {string}
     */
    getTime(dateStr) {
      const d = new Date(dateStr);
      return `${d.getHours().toString().padStart(2, "0")}:${d
        .getMinutes()
        .toString()
        .padStart(2, "0")}`;
    },

    /**
     * @description Nombre del doctor del turno
     * @param {Object} apt - Turno
     * @returns {string}
     */
    getAptDoctorName(apt) {
      // UC-REG-262 — turno de trámite por institución: se ve la institución.
      if (hidesAppointmentDoctor(apt)) return appointmentInstitutionName(apt);
      const doc = apt.doctor || apt.professional;
      if (!doc) return "Sin asignar";
      return `Dr. ${doc.lastName || ""}, ${doc.firstName || ""}`.trim();
    },

    /**
     * @description Especialidad del doctor del turno
     * @param {Object} apt - Turno
     * @returns {string}
     */
    getAptSpecialty(apt) {
      if (hidesAppointmentDoctor(apt)) return "";
      const doc = apt.doctor || apt.professional;
      return doc?.specialty || doc?.specialization || "";
    },

    /**
     * @description Tipo friendly del turno
     * @param {Object} apt - Turno
     * @returns {string}
     */
    getAptType(apt) {
      return apt.type === "virtual" ? "Virtual" : "Presencial";
    },

    /**
     * @description Nombre del medicamento de la receta
     * @param {Object} med - Indicación de medicamento
     * @returns {string}
     */
    getMedName(med) {
      return (
        med.medication?.name ||
        med.publicMedication?.name ||
        med.description ||
        "Medicamento"
      );
    },

    /**
     * @description Detalle de la receta (doctor + posología)
     * @param {Object} med - Indicación de medicamento
     * @returns {string}
     */
    getMedDetail(med) {
      const parts = [];
      if (med.doctor) {
        parts.push(
          `Dr. ${med.doctor.lastName || ""}, ${
            med.doctor.firstName || ""
          }`.trim()
        );
      }
      const posology = [med.dose, med.frequency, med.duration]
        .filter(Boolean)
        .join(" ");
      if (posology) parts.push(posology);
      return parts.join(" · ");
    },

    /**
     * @description Formatea fecha de la receta
     * @param {string} dateStr - Fecha ISO
     * @returns {string}
     */
    formatMedDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return d.toLocaleDateString("es-AR", {
        day: "2-digit",
        month: "short",
      });
    },
  },
};
</script>

<template lang="pug">
div
  //- Admin dashboard
  template(v-if="isAdminLike")
    //- UC-REG-249: cada widget OOSS va gateado por su feature (default-ON).
    //- Si TODO el dashboard OOSS está apagado (ej. Campana), fallback de
    //- acciones rápidas para que el Home no quede vacío.
    template(v-if="hasAnyOossDashboardData")
      .dashboard-stats(v-loading="isLoadingDashboard")
        .stat-card(v-has-feature="'ooss.members'")
          .stat-card__icon.stat-card__icon--blue
            micon(name="people")
          .stat-card__content
            .stat-card__value {{ stats.activeMembers | numFormat }}
            .stat-card__label Afiliados activos

        .stat-card(v-has-feature="'ooss.authorizations'")
          .stat-card__icon.stat-card__icon--teal
            micon(name="check")
          .stat-card__content
            .stat-card__value {{ stats.authorizationsToday | numFormat }}
            .stat-card__label Autorizaciones hoy

        .stat-card(v-has-feature="'ooss.providers'")
          .stat-card__icon.stat-card__icon--purple
            micon(name="stethoscope")
          .stat-card__content
            .stat-card__value {{ stats.activeProviders | numFormat }}
            .stat-card__label Prestadores en red

        .stat-card(v-has-feature="'ooss.audit'")
          .stat-card__icon.stat-card__icon--orange
            micon(name="pending_actions")
          .stat-card__content
            .stat-card__value {{ stats.pendingAudit | numFormat }}
            .stat-card__label Pendientes auditoría

    template(v-else)
      //- UC-REG-261: sin acciones (todas apagadas por feature) no queda el título huérfano.
      h3.doctor-section-title(v-if="adminQuickActions.length") Acciones rápidas
      .doctor-actions(v-if="adminQuickActions.length")
        .action-card(
          v-for="action in adminQuickActions"
          :key="action.route"
          @click="goToAction(action.route)"
        )
          .action-card__icon
            i(:class="action.icon")
          .action-card__title {{ action.title }}
          .action-card__subtitle {{ action.subtitle }}

    .dashboard-grid(v-if="hasAnyOossDashboardData")
      //- Autorizaciones recientes
      .dashboard-card(v-has-feature="'ooss.authorizations'")
        .dashboard-card__header
          h3.dashboard-card__title Autorizaciones pendientes
        .dashboard-card__body
          table.dashboard-table
            thead
              tr
                th N°
                th Afiliado
                th Prestación
                th Prestador
                th Estado
                th Fecha
            tbody
              tr(v-for="auth in recentAuthorizations" :key="auth._id || auth.caseNumber")
                td.dashboard-table__mono {{ auth.caseNumber || '—' }}
                td.dashboard-table__bold {{ auth.memberSnapshot ? auth.memberSnapshot.lastName + ', ' + auth.memberSnapshot.firstName : '—' }}
                td {{ auth.practiceSnapshot ? auth.practiceSnapshot.name : '—' }}
                td {{ auth.providerSnapshot ? auth.providerSnapshot.lastName + ', ' + auth.providerSnapshot.firstName : '—' }}
                td
                  span.badge(:class="getStatusClass(auth.status)") {{ getStatusLabel(auth.status) }}
                td {{ formatShortDate(auth.createdAt) }}

      //- Transacciones recientes (vienen del validador de prestaciones)
      .dashboard-card(v-has-feature="'ooss.validator'")
        .dashboard-card__header
          h3.dashboard-card__title Transacciones recientes
        .dashboard-card__body
          .activity-list
            .activity-item(v-for="item in recentActivity" :key="item._id || item.transactionCode")
              .activity__dot(:class="getActivityClass(item.origin === 'authorization' ? 'info' : 'success')")
              .activity__content
                .activity__text
                  span.activity__code {{ item.transactionCode || '—' }}
                  |  — {{ item.memberSnapshot ? item.memberSnapshot.lastName + ', ' + item.memberSnapshot.firstName : '—' }} — {{ item.practiceSnapshot ? item.practiceSnapshot.name : '—' }}
                .activity__time {{ timeAgo(item.createdAt) }}

  //- Doctor / Secretary / Clinical dashboard
  template(v-else-if="isDoctorDashboard")
    .doctor-dashboard
      //- Stats
      .doctor-stats(v-loading="isLoadingDoctorStats")
        //- UC-REG-261: "Pacientes últimos 30 días" cuenta slots con turno
        //- (atenciones vía agenda) → mismo flag que la Agenda y que "Turnos
        //- próx. 7 días". Solo este stat: los demás siguen con su propio flag.
        .doctor-stat(v-has-feature="'clinic.appointments'")
          .doctor-stat__value {{ doctorStats.patientsLast30 | numFormat }}
          .doctor-stat__label PACIENTES ÚLTIMOS 30 DÍAS
        //- UC-REG-261: stats de atenciones y turnos respetan los mismos flags
        //- que sus módulos (sidebar "Atenciones" / "Agenda"), como el de
        //- validaciones con ooss.validator (UC-REG-249).
        .doctor-stat.doctor-stat--blue(v-has-feature="'clinic.attentions'")
          .doctor-stat__value {{ doctorStats.todayAppointments | numFormat }}
          .doctor-stat__label ATENCIONES HOY
        .doctor-stat.doctor-stat--purple(v-has-feature="'clinic.appointments'")
          .doctor-stat__value {{ doctorStats.next7DaysSlots | numFormat }}
          .doctor-stat__label TURNOS PRÓX. 7 DÍAS
        .doctor-stat.doctor-stat--teal(v-has-feature="'ooss.validator'")
          .doctor-stat__value {{ doctorStats.monthValidations | numFormat }}
          .doctor-stat__label VALIDACIONES DEL MES

      //- Quick actions. UC-REG-261: sin acciones (todas apagadas por
      //- feature, Campana) no queda el título huérfano.
      h3.doctor-section-title(v-if="quickActions.length") Acciones rápidas
      .doctor-actions(v-if="quickActions.length")
        .action-card(
          v-for="action in quickActions"
          :key="action.route"
          @click="goToAction(action.route)"
        )
          .action-card__icon
            i(:class="action.icon")
          .action-card__title {{ action.title }}
          .action-card__subtitle {{ action.subtitle }}

  //- Patient dashboard
  template(v-else-if="isPatient")
    .ph
      //- Header: saludo + fecha
      .ph__header
        span.ph__date {{ todayDate }}

      //- UC-REG-295: banner de la credencial municipal (Campana)
      municipal-credential-card(
        v-if="isMunicipalCredential"
        :person="municipalCredential"
        compact
        @click="$router.push({ name: 'patient-credential' })"
      )

      //- Mini credencial (solo si es afiliado)
      .ph__credential(v-if="isMember && memberData" @click="$router.push({ name: 'patient-credential' })")
        .ph__credential-left
          .ph__credential-badge Afiliado activo
          .ph__credential-number {{ credentialNumber }}
          .ph__credential-name {{ credentialName }}
          .ph__credential-plan(v-if="credentialPlan") {{ credentialPlan }}
        .ph__credential-arrow
          micon(name="chevron_right")

      //- Acciones rápidas
      .ph__actions
        //- UC-REG-304: sin "Tareas de salud" en el portal solo-prescripciones.
        .ph__action-card(v-if="patientPortal.showHealthTasksCard" @click="$router.push({ name: 'patient-my-tasks' })")
          .ph__action-icon
            micon(name="fact_check")
          .ph__action-title Tareas de salud
          .ph__action-sub Tu plan del día

        .ph__action-card(@click="$router.push({ name: 'patient-turnos' })")
          .ph__action-icon
            micon(name="calendar")
          .ph__action-title Gestionar cita
          .ph__action-sub Reservá o gestioná tus turnos

        .ph__action-card(@click="$router.push({ name: 'patient-me' })")
          .ph__action-icon
            micon(name="assignment")
          .ph__action-title {{ patientPortal.medicalRecordCard.title }}
          .ph__action-sub {{ patientPortal.medicalRecordCard.subtitle }}

        .ph__action-card(@click="$router.push({ name: 'profile' })")
          .ph__action-icon
            micon(name="person")
          .ph__action-title Editar mi información
          .ph__action-sub Datos personales

        //- UC-REG-314: "Mis patologías" solo si la API expone la capacidad.
        .ph__action-card(v-if="hasCapability('patientChronicConditions')" @click="$router.push({ name: 'patient-my-conditions' })")
          .ph__action-icon
            micon(name="favorite")
          .ph__action-title Mis patologías
          .ph__action-sub Para tu seguimiento y prevención

      //- Próximos turnos
      .ph__section
        .ph__section-header
          .ph__section-left
            h2.ph__section-title Próximos turnos
            span.ph__badge {{ upcomingAppointments.length }}
          router-link.ph__link(:to="{ name: 'patient-appointments' }") Ver todos

        div(v-loading="isLoadingAppointments")
          template(v-if="upcomingAppointments.length > 0")
            .ph__apt-card(v-for="apt in upcomingAppointments" :key="apt._id")
              .ph__apt-date
                span.ph__apt-day {{ getDay(apt.startsAt) }}
                span.ph__apt-month {{ getMonth(apt.startsAt) }}
              .ph__apt-info
                span.ph__apt-doctor {{ getAptDoctorName(apt) }}
                span.ph__apt-specialty(v-if="getAptSpecialty(apt)") {{ getAptSpecialty(apt) }}
              .ph__apt-right
                span.ph__apt-time {{ getTime(apt.startsAt) }}
                span.ph__apt-type {{ getAptType(apt) }}

          p.ph__empty(v-else-if="!isLoadingAppointments") No tenés turnos próximos

      //- Recetas emitidas
      .ph__section
        .ph__section-header
          .ph__section-left
            h2.ph__section-title Recetas emitidas
            span.ph__badge {{ patientMedications.length }}
          router-link.ph__link(:to="{ name: 'patient-me' }") Ver todas

        div(v-loading="isLoadingMedications")
          template(v-if="recentMedications.length > 0")
            .ph__med-card(v-for="med in recentMedications" :key="med._id")
              .ph__med-icon
                micon(name="medication")
              .ph__med-info
                span.ph__med-name {{ getMedName(med) }}
                span.ph__med-detail {{ getMedDetail(med) }}
              span.ph__med-date {{ formatMedDate(med.createdAt) }}

          p.ph__empty(v-else-if="!isLoadingMedications") No tenés recetas emitidas

  //- Fallback
  template(v-else)
    BaseSubNav
      template(#title)
        | {{ userName }}
</template>

<style lang="scss" scoped>
/* ============================================
   ADMIN DASHBOARD
   ============================================ */
.dashboard-greeting {
  font-size: 24px;
  font-weight: 700;
  color: $navy;
  margin-bottom: 24px;
}

.dashboard-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 24px;
}

.stat-card {
  background: #fff;
  border: 1px solid $border-color;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    .micon {
      width: 24px;
      height: 24px;
    }

    &--blue {
      background: #dbeafe;
      .micon {
        fill: #1e40af;
      }
    }
    &--teal {
      background: $teal-light;
      .micon {
        fill: $navy;
      }
    }
    &--purple {
      background: #ede9fe;
      .micon {
        fill: #6d28d9;
      }
    }
    &--orange {
      background: #fef3c7;
      .micon {
        fill: #92400e;
      }
    }
  }

  &__value {
    font-size: 28px;
    font-weight: 800;
    color: $text-primary-new;
    line-height: 1;
  }

  &__label {
    font-size: 13px;
    color: $text-secondary-new;
    margin-top: 4px;
  }
}

.dashboard-grid {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 20px;
}

.dashboard-card {
  background: #fff;
  border: 1px solid $border-color;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  overflow: hidden;

  &__header {
    padding: 16px 20px;
    border-bottom: 1px solid $border-color;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: $text-primary-new;
    margin: 0;
  }

  &__body {
    padding: 0;
  }
}

.dashboard-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  th {
    position: static;
    box-shadow: none;
    background: $header-bg;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #94a3b8;
    padding: 8px 12px;
    border-bottom: 1px solid $border-color;
    white-space: nowrap;

    &::after {
      display: none;
    }
  }

  td {
    padding: 8px 12px;
    font-size: 12px;
    color: $text-primary-new;
    border-bottom: 1px solid #f1f5f9;
    height: auto;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__bold {
    font-weight: 600;
  }

  &__mono {
    font-family: monospace;
    font-weight: 700;
    color: $navy;
    font-size: 11px;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.activity-list {
  padding: 8px 0;
}

.activity-item {
  display: flex;
  gap: 12px;
  padding: 12px 20px;
  transition: background 0.15s ease;
  &:hover {
    background: #f8fafc;
  }
}

.activity__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 6px;
  flex-shrink: 0;
  &--info {
    background: #3b82f6;
  }
  &--success {
    background: #22c55e;
  }
  &--warning {
    background: #f59e0b;
  }
  &--danger {
    background: #ef4444;
  }
}

.activity__code {
  font-family: monospace;
  font-weight: 700;
  color: $navy;
  font-size: 12px;
}

.activity__text {
  font-size: 13px;
  color: $text-primary-new;
  line-height: 1.4;
}

.activity__time {
  font-size: 12px;
  color: $text-muted-new;
  margin-top: 2px;
}

/* ============================================
   DOCTOR / SECRETARY DASHBOARD
   ============================================ */
.doctor-dashboard {
  padding: 0;
}

.doctor-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 28px;
}

.doctor-stat {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 16px;
  text-align: center;
  border-left: 3px solid #e2e8f0;
  &--blue {
    border-left-color: #3b82f6;
  }
  &--purple {
    border-left-color: #8b5cf6;
  }
  &--teal {
    border-left-color: #05e5ba;
  }

  &__value {
    font-size: 20px;
    font-weight: 700;
    color: #002d5e;
    line-height: 1;
  }

  &__label {
    font-size: 10px;
    font-weight: 600;
    color: #6b7294;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 4px;
  }
}

.doctor-section-title {
  font-size: 16px;
  font-weight: 700;
  color: #002d5e;
  margin: 0 0 16px;
}

.doctor-actions {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.action-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #05e5ba;
    background: #e8fdf8;
  }

  &__icon {
    font-size: 28px;
    color: #002d5e;
    margin-bottom: 12px;
    i {
      font-size: 28px;
    }
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: #002d5e;
  }

  &__subtitle {
    font-size: 12px;
    color: #6b7294;
    margin-top: 4px;
  }
}

/* ============================================
   PATIENT HOME (.ph)
   ============================================ */
.ph {
  max-width: 800px;
  margin: 0 auto;

  &__header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 24px;
  }

  &__greeting {
    font-size: 22px;
    font-weight: 800;
    color: $navy;
    margin: 0;
  }

  &__date {
    font-size: 13px;
    color: #6b7280;
  }

  // Mini credencial
  &__credential {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: linear-gradient(135deg, #002d5e 0%, #1a4a7a 50%, #2a6db5 100%);
    border-radius: 12px;
    padding: 16px 20px;
    margin-bottom: 16px;
    color: #fff;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
    position: relative;
    overflow: hidden;

    &::after {
      content: "";
      position: absolute;
      top: -30px;
      right: -30px;
      width: 100px;
      height: 100px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.04);
      pointer-events: none;
    }

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 16px rgba(0, 45, 94, 0.25);
    }
  }

  &__credential-left {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__credential-badge {
    display: inline-flex;
    align-self: flex-start;
    padding: 2px 8px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.15);
    font-size: 10px;
    font-weight: 600;
    margin-bottom: 4px;
  }

  &__credential-number {
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 1.5px;
    font-family: "Plus Jakarta Sans", monospace;
  }

  &__credential-name {
    font-size: 13px;
    font-weight: 600;
    opacity: 0.9;
  }

  &__credential-plan {
    font-size: 11px;
    opacity: 0.6;
  }

  &__credential-arrow {
    flex-shrink: 0;
    .micon {
      width: 20px;
      height: 20px;
      fill: rgba(255, 255, 255, 0.5);
    }
  }

  // Acciones rápidas
  &__actions {
    display: grid;
    // 4 accesos → 2×2 (balanceado y respira). En mobile cae a 1 col (media query abajo).
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 28px;
  }

  &__action-card {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 16px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s;
    text-decoration: none;
    color: inherit;

    &:hover {
      border-color: #0d9d9d;
      background: #f0fdfa;
    }
  }

  &__action-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;

    .micon {
      width: 20px;
      height: 20px;
      fill: $navy;
    }
  }

  &__action-title {
    font-size: 13px;
    font-weight: 700;
    color: $navy;
  }

  &__action-sub {
    font-size: 11px;
    color: #6b7280;
    margin-top: 2px;
  }

  // Secciones
  &__section {
    margin-bottom: 24px;
  }

  &__section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  &__section-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__section-title {
    font-size: 14px;
    font-weight: 700;
    color: $navy;
    margin: 0;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 10px;
    background: #f3f4f6;
    color: #6b7280;
    font-size: 11px;
    font-weight: 700;
  }

  &__link {
    font-size: 12px;
    font-weight: 600;
    color: #0d9d9d;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }

  &__empty {
    font-size: 13px;
    color: #9ca3af;
    padding: 16px 0;
    margin: 0;
  }

  // Turnos
  &__apt-card {
    display: flex;
    align-items: center;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 8px;
    transition: box-shadow 0.2s;
    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    }
  }

  &__apt-date {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 52px;
    min-height: 56px;
    background: $navy;
    color: #fff;
    flex-shrink: 0;
    padding: 6px 0;
  }

  &__apt-day {
    font-size: 20px;
    font-weight: 700;
    line-height: 1;
  }

  &__apt-month {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 2px;
  }

  &__apt-info {
    display: flex;
    flex-direction: column;
    padding: 10px 14px;
    flex: 1;
    min-width: 0;
    gap: 1px;
  }

  &__apt-doctor {
    font-size: 13px;
    font-weight: 700;
    color: $navy;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__apt-specialty {
    font-size: 11px;
    color: #6b7280;
  }

  &__apt-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    padding: 10px 14px;
    flex-shrink: 0;
    gap: 4px;
  }

  &__apt-time {
    font-size: 12px;
    font-weight: 700;
    color: $navy;
  }

  &__apt-type {
    font-size: 10px;
    font-weight: 600;
    color: #6b7280;
    background: #f3f4f6;
    padding: 2px 6px;
    border-radius: 4px;
  }

  // Recetas
  &__med-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    margin-bottom: 8px;
  }

  &__med-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    .micon {
      width: 18px;
      height: 18px;
      fill: $navy;
    }
  }

  &__med-info {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }

  &__med-name {
    font-size: 12px;
    font-weight: 700;
    color: $navy;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__med-detail {
    font-size: 10px;
    color: #6b7280;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__med-date {
    font-size: 10px;
    color: #6b7280;
    flex-shrink: 0;
  }
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 768px) {
  .dashboard-stats,
  .doctor-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .dashboard-grid {
    grid-template-columns: 1fr;
  }

  .doctor-actions {
    grid-template-columns: repeat(2, 1fr);
  }

  // Patient home mobile
  .ph {
    &__header {
      flex-direction: column;
      gap: 2px;
      margin-bottom: 16px;
    }

    &__greeting {
      font-size: 18px;
    }

    &__credential {
      padding: 14px 16px;
      border-radius: 10px;
      margin-bottom: 12px;
    }

    &__credential-number {
      font-size: 15px;
      letter-spacing: 1px;
    }

    &__credential-name {
      font-size: 12px;
    }

    &__actions {
      gap: 8px;
      margin-bottom: 20px;
    }

    &__action-card {
      padding: 12px;
      align-items: center;
      text-align: center;
    }

    &__action-icon {
      margin-bottom: 6px;
    }

    &__action-sub {
      display: none;
    }

    &__action-title {
      font-size: 11px;
    }

    &__apt-date {
      width: 44px;
      min-height: 48px;
    }

    &__apt-day {
      font-size: 17px;
    }
    &__apt-month {
      font-size: 9px;
    }
    &__apt-doctor {
      font-size: 12px;
    }
    &__apt-time {
      font-size: 11px;
    }

    &__med-card {
      padding: 8px 10px;
      gap: 8px;
    }

    &__med-icon {
      width: 28px;
      height: 28px;
    }

    &__med-name {
      font-size: 11px;
    }
    &__med-detail {
      font-size: 9px;
    }
  }
}
</style>
